import { ChevronDownIcon } from "lucide-react";
import { useCallback, useRef, useState, type RefObject } from "react";

import type { TimelineScrollHandle } from "./useTimelineScroll";

import { t } from "~/i18n";

export interface TimelineAutoScroll {
  isAtEndRef: RefObject<boolean>;
  showScrollToBottom: boolean;
  onIsAtEndChange: (isAtEnd: boolean) => void;
  scrollToEnd: (animated?: boolean) => void;
  resetToEnd: () => void;
}

/**
 * Scroll-follow state around a MessagesTimeline: the timeline's own scroller
 * pins to the bottom while the reader is there; this tracks whether the reader
 * scrolled away and drives the scroll-to-bottom pill.
 */
export function useTimelineAutoScroll(
  timelineRef: RefObject<TimelineScrollHandle | null>,
): TimelineAutoScroll {
  const isAtEndRef = useRef(true);
  const [showScrollToBottom, setShowScrollToBottom] = useState(false);

  const onIsAtEndChange = useCallback((isAtEnd: boolean) => {
    if (isAtEndRef.current === isAtEnd) return;
    isAtEndRef.current = isAtEnd;
    setShowScrollToBottom(!isAtEnd);
  }, []);

  const scrollToEnd = useCallback(
    (animated = false) => {
      timelineRef.current?.scrollToEnd({ animated });
    },
    [timelineRef],
  );

  const resetToEnd = useCallback(() => {
    isAtEndRef.current = true;
    setShowScrollToBottom(false);
  }, []);

  return { isAtEndRef, showScrollToBottom, onIsAtEndChange, scrollToEnd, resetToEnd };
}

export function TimelineScrollToBottomPill({ onClick }: { onClick: () => void }) {
  return (
    <div className="pointer-events-none absolute bottom-1 left-1/2 z-30 flex -translate-x-1/2 justify-center py-1.5">
      <button
        type="button"
        onClick={onClick}
        className="pointer-events-auto flex items-center gap-1.5 rounded-full border border-border/60 bg-card px-3 py-1 text-muted-foreground text-xs shadow-sm transition-colors hover:border-border hover:text-foreground hover:cursor-pointer"
      >
        <ChevronDownIcon className="size-3.5" />
        {t("common/scrollToBottom_kgvywf")}
      </button>
    </div>
  );
}
